// 警告：変わったらすぐに読み直し（WebSocket）、新しい強い警告は音とお知らせで知らせる
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { RULES, type Alert } from '@torapon/shared';
import { api } from './api';
import { useToast } from '../components/ui';
import { useNames } from './directory';
import { useMe } from './me';
import { putNote } from './fs';
import { useLive } from './realtime';

type Ctx = { alerts: Alert[]; reload: () => void; ack: (id: string, memo?: string) => Promise<void> };
type Names = ReturnType<typeof useNames>;
const hm = (min: number) => `${Math.floor(min / 60)} 時間${min % 60 ? ` ${min % 60} 分` : ''}`;

/** 警告の文言（名前・場所は Firestore から引く） */
export function alertText(a: Alert, n: Names): string {
  const who = n.driver(a.driverId);
  const place = n.place(a.placeId);
  switch (a.kind) {
    case 'alcohol': return `${who}さんの点呼でアルコールが検出されました`;
    case 'rollcall_waiting': return `${who}さんの点呼が確認を待っています`;
    case 'unassigned': return `${a.deliveryNo}（${place}行き）が ${a.minutes} 分間ドライバー未選択です`;
    case 'unaccepted': return `${who}さんに送った依頼（${a.deliveryNo}）が ${a.minutes} 分経っても受付されていません`;
    case 'not_working': return `${who}さんは勤務していませんが、${a.deliveryNo} が割り当てられています`;
    case 'due_over': return `${a.driverId ? `${who}さんの ` : ''}${a.deliveryNo}（${place}）が必着 ${a.dueAt} を過ぎました。まだ届いていません`;
    case 'unknown_stop': return `${who}さんが理由不明のまま ${a.minutes} 分停車しています`;
    case 'continuous_drive': return `${who}さんの連続運転が ${hm(a.minutes ?? 0)}になりました`;
    case 'device_pending': return `${who}さんの端末が承認を待っています`;
    case 'route_changed': return `順番変更：${who}さんが回る順番を変えました`;
    case 'schedule_unassigned': return `${a.dueAt} に送る定期便の担当が決まっていません`;
    case 'schedule_unsent': return `${who}さんへの定期便（${a.dueAt}）が送れていません。勤務していない可能性があります`;
    case 'schedule_driver_off': return `${who}さんは休みの日ですが、定期便（${a.dueAt}）の担当になっています`;
    case 'tomorrow_unchecked': return `明日の予定（${a.minutes} 件）をまだ確かめていません。「予定」で確かめてください`;
    case 'chat_unread': return `${who}さんからのメッセージが ${a.minutes} 分読まれていません`;
    case 'delete_request': return `${who}さんからアカウント削除の申請があります`;
    case 'rest_short': return `${who}さんは前回の勤務から ${hm(a.minutes ?? 0)}しか経っていません${(a.minutes ?? 0) < RULES.minRestHours * 60 ? `。${RULES.minRestHours} 時間未満は勤務を開始できません` : ''}`;
    case 'dev_mode': return '開発者モードの解除が有効です（テスト用）';
    case 'issue': return `${who}さんから問題の報告があります（${a.deliveryNo}）`;
    case 'no_signal': return `${who}さんの位置が ${a.minutes} 分間届いていません`;
    case 'restraint': return `${who}さんの拘束時間が ${hm(a.minutes ?? 0)}になりました${(a.minutes ?? 0) >= 15 * 60 ? '。勤務終了の押し忘れの可能性があります' : ''}`;
  }
}
const AlertsCtx = createContext<Ctx>({ alerts: [], reload: () => {}, ack: async () => {} });
export const useAlerts = () => useContext(AlertsCtx);

function chime() {
  try {
    const ac = new AudioContext();
    let t = ac.currentTime;
    for (const f of [880, 660, 880]) {
      const o = ac.createOscillator(), g = ac.createGain();
      o.frequency.value = f; g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.2, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
      o.connect(g).connect(ac.destination); o.start(t); o.stop(t + 0.22); t += 0.2;
    }
  } catch { /* 音が出せない環境では鳴らさない */ }
}

export function AlertsProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const names = useNames();
  const namesRef = useRef(names);
  namesRef.current = names;
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const seen = useRef<Set<string> | null>(null);
  const reload = useCallback(() => {
    api.get<{ alerts: Alert[] }>('/v1/office/alerts').then(({ alerts: list }) => {
      setAlerts(list);
      const fresh = seen.current ? list.filter((a) => a.level === 'alert' && !seen.current!.has(a.id)) : [];
      seen.current = new Set(list.map((a) => a.id));
      if (fresh.length) { chime(); fresh.slice(0, 3).forEach((a) => toast(alertText(a, namesRef.current))); }
    }).catch(() => {});
  }, [toast]);
  useEffect(() => { reload(); }, [reload]);
  // 拘束時間・未受付の分などは時間で変わるので、状況が変わったときにも読み直す
  useLive(['alerts', 'status', 'deliveries', 'rollcalls'], reload);
  const me = useMe();
  // 対応のメモは Firestore の notes（取引先の名前が入りうるため）
  const ack = async (id: string, memo?: string) => {
    await api.post(`/v1/office/alerts/${encodeURIComponent(id)}/ack`, {});
    if (memo && me) await putNote(`ack_${id.replace(/[^\w-]/g, '_')}`, { officeId: me.officeId, kind: 'alertAck', text: memo }).catch(() => {});
    reload();
  };
  return <AlertsCtx.Provider value={{ alerts, reload, ack }}>{children}</AlertsCtx.Provider>;
}
